import { useCallback, useMemo } from "react";
import { AuthApiClient } from "@/lib/api/clients/auth-api-client";
import { AuthService } from "@/lib/services/auth-service";
import {
  LoginRequest,
  RegisterRequest,
  TokenResponse,
  VerifyEmailRequest,
  ResetPasswordRequest,
  ResendVerificationEmailRequest,
  MessageResponse,
  PublicConfigResponse,
} from "@/lib/types/api/auth-types";
import { User } from "@/lib/types/api/user-types";
import { toast } from "@/hooks/use-toast";
import { useTranslations } from "next-intl";

/**
 * React hook for authentication service operations
 * Follows SOC: Separates auth logic from UI components
 * Follows DRY: Centralized error handling and service instantiation
 */
export function useAuthService() {
  // Translations
  const t = useTranslations("AuthError");
  const tCommon = useTranslations("Common");

  // Create service instance with dependency injection
  const authService = useMemo(() => {
    const authApiClient = new AuthApiClient();
    return new AuthService(authApiClient);
  }, []);

  // Basic authentication flows
  const login = useCallback(
    async (data: LoginRequest): Promise<TokenResponse | null> => {
      try {
        const result = await authService.login(data);
        if (result.success) {
          toast({
            title: tCommon("success"),
            description: t("loginSuccessful"),
          });
          return result.data || null;
        } else {
          toast({
            title: tCommon("error"),
            description: result.error?.message || "Login failed",
            variant: "destructive",
          });
          return null;
        }
      } catch (error) {
        toast({
          title: tCommon("error"),
          description: t("unexpectedErrorDuringLogin"),
          variant: "destructive",
        });
        return null;
      }
    },
    [authService, t, tCommon]
  );

  const register = useCallback(
    async (data: RegisterRequest): Promise<User | null> => {
      try {
        const result = await authService.register(data);
        if (result.success) {
          toast({
            title: tCommon("success"),
            description: "Registration successful, please verify your email",
          });
          return result.data || null;
        } else {
          toast({
            title: tCommon("error"),
            description: result.error?.message || "Registration failed",
            variant: "destructive",
          });
          return null;
        }
      } catch (error) {
        toast({
          title: tCommon("error"),
          description: t("unexpectedErrorDuringRegistration"),
          variant: "destructive",
        });
        return null;
      }
    },
    [authService, t, tCommon]
  );

  const verifyEmail = useCallback(
    async (data: VerifyEmailRequest): Promise<boolean> => {
      try {
        const result = await authService.verifyEmail(data);
        if (result.success) {
          toast({
            title: tCommon("success"),
            description: t("emailVerifiedSuccessfully"),
          });
          return true;
        } else {
          toast({
            title: tCommon("error"),
            description: result.error?.message || "Email verification failed",
            variant: "destructive",
          });
          return false;
        }
      } catch (error) {
        toast({
          title: tCommon("error"),
          description: t("unexpectedErrorDuringEmailVerification"),
          variant: "destructive",
        });
        return false;
      }
    },
    [authService, t, tCommon]
  );

  const requestPasswordRecovery = useCallback(
    async (email: string): Promise<boolean> => {
      try {
        const result = await authService.requestPasswordRecovery(email);
        if (result.success) {
          toast({
            title: tCommon("success"),
            description: t("passwordRecoveryEmailSent"),
          });
          return true;
        } else {
          toast({
            title: tCommon("error"),
            description:
              result.error?.message || "Password recovery request failed",
            variant: "destructive",
          });
          return false;
        }
      } catch (error) {
        toast({
          title: tCommon("error"),
          description: t("unexpectedError"),
          variant: "destructive",
        });
        return false;
      }
    },
    [authService, t, tCommon]
  );

  const resetPassword = useCallback(
    async (data: ResetPasswordRequest): Promise<boolean> => {
      try {
        const result = await authService.resetPassword(data);
        if (result.success) {
          toast({
            title: tCommon("success"),
            description: t("passwordResetSuccessfully"),
          });
          return true;
        } else {
          toast({
            title: tCommon("error"),
            description: result.error?.message || "Password reset failed",
            variant: "destructive",
          });
          return false;
        }
      } catch (error) {
        toast({
          title: tCommon("error"),
          description: t("unexpectedErrorDuringPasswordReset"),
          variant: "destructive",
        });
        return false;
      }
    },
    [authService, t, tCommon]
  );

  const resendVerificationEmail = useCallback(
    async (data: ResendVerificationEmailRequest): Promise<boolean> => {
      try {
        const result = await authService.resendVerificationEmail(data);
        if (result.success) {
          toast({
            title: tCommon("success"),
            description: t("verificationEmailSent"),
          });
          return true;
        } else {
          toast({
            title: tCommon("error"),
            description:
              result.error?.message || "Failed to send verification email",
            variant: "destructive",
          });
          return false;
        }
      } catch (error) {
        toast({
          title: tCommon("error"),
          description: t("unexpectedError"),
          variant: "destructive",
        });
        return false;
      }
    },
    [authService, t, tCommon]
  );

  // Configuration
  const getPublicConfig =
    useCallback(async (): Promise<PublicConfigResponse | null> => {
      try {
        const result = await authService.getPublicConfig();
        if (result.success) {
          return result.data || null;
        } else {
          toast({
            title: tCommon("error"),
            description:
              result.error?.message || "Failed to get public configuration",
            variant: "destructive",
          });
          return null;
        }
      } catch (error) {
        toast({
          title: tCommon("error"),
          description: t("unexpectedError"),
          variant: "destructive",
        });
        return null;
      }
    }, [authService, t, tCommon]);

  return {
    // Basic auth flows
    login,
    register,
    verifyEmail,
    requestPasswordRecovery,
    resetPassword,
    resendVerificationEmail,

    // Configuration
    getPublicConfig,
  };
}
